import React from 'react';
import { useLocation, useParams } from 'react-router';
import { LscAuthGate } from '../hooks/useLscAuthGate';
import { useLeague } from '../hooks/useLeague';
import LoadingState from '../components/LoadingState';
import ErrorBanner from '../components/ErrorBanner';
import LeagueHomePage from '../components/leagueHome/LeagueHomePage';
import StreamLeagueHomePage from '../components/stream/StreamLeagueHomePage';
import { shouldRenderStreamPages } from '../stream/streamPages';
import { getDashboardRenderState } from '../leagueDashboardState';
import { lscReturnTo } from '../routing';
import { COMMISSIONER_ONLY_REDIRECT_STATE } from '../components/CommissionerOnlyGate';
import { COMMISSIONER_ONLY_NOTICE } from '../constants';
import LscInstallBanner from '../components/LscInstallBanner';

const LscLeagueDashboardPage: React.FC = () => {
  const { leagueId } = useParams<{ leagueId: string }>();
  return (
    <LscAuthGate returnTo={lscReturnTo(`/leagues/${leagueId}`)}>
      <LeagueHomeContent />
    </LscAuthGate>
  );
};

function LeagueHomeContent() {
  const { leagueId } = useParams<{ leagueId: string }>();
  const location = useLocation();
  const { league, isCommissioner, isOperator, isOwner, loading, error } = useLeague();
  const commissionerNotice =
    (location.state as Record<string, boolean> | null)?.[COMMISSIONER_ONLY_REDIRECT_STATE] ===
    true;

  const renderState = getDashboardRenderState({
    loading,
    error,
    hasLeague: Boolean(league),
  });

  if (renderState === 'loading') {
    return <LoadingState message="Loading league home..." />;
  }
  if (renderState === 'error') {
    return <ErrorBanner message={error!} />;
  }
  if (renderState === 'missing') {
    return <ErrorBanner message="League workspace could not be loaded." />;
  }

  return (
    <div className="space-y-8">
      {commissionerNotice ? (
        <div
          className="rounded-lg border border-primary-700 bg-primary-900/30 p-4 text-sm text-primary-100"
          data-testid="commissioner-only-notice"
          role="status"
        >
          {COMMISSIONER_ONLY_NOTICE}
        </div>
      ) : null}

      <LscInstallBanner mobileOnly variant="banner" />

      {shouldRenderStreamPages({
        setting: league?.stream_pages,
        search: location.search,
        previewKey: 'home',
        viewer: { isOwner, isCommissioner },
      }) ? (
        <StreamLeagueHomePage
          leagueId={leagueId!}
          league={league!}
          isCommissioner={isCommissioner}
          isOperator={isOperator}
          isOwner={isOwner}
        />
      ) : (
        <LeagueHomePage
          leagueId={leagueId!}
          league={league!}
          isCommissioner={isCommissioner}
          isOperator={isOperator}
          isOwner={isOwner}
        />
      )}
    </div>
  );
}

export default LscLeagueDashboardPage;
